import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Lightning, House, Barbell, Brain, Flame, Users, User,
  List, X, Wind, ChartLineUp, ChartBar, Wrench, Handshake, Crown, WifiSlash, Trophy, Gift, Medal
} from '@phosphor-icons/react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: House },
  { path: '/workouts', label: 'Workouts', icon: Barbell },
  { path: '/workouts/custom', label: 'My Workouts', icon: Wrench },
  { path: '/workouts/history', label: 'History', icon: ChartLineUp },
  { path: '/analytics', label: 'Analytics', icon: ChartBar },
  { path: '/ai-plan', label: 'AI Plan', icon: Brain },
  { path: '/nutrition', label: 'Nutrition', icon: Flame },
  { path: '/stress-reset', label: 'Stress Reset', icon: Wind },
  { path: '/challenges', label: 'Challenges', icon: Users },
  { path: '/leaderboards', label: 'Leaderboards', icon: Trophy },
  { path: '/achievements', label: 'Achievements', icon: Medal },
  { path: '/buddy', label: 'Buddy', icon: Handshake },
  { path: '/referrals', label: 'Refer & Earn', icon: Gift },
  { path: '/pricing', label: 'Go Pro', icon: Crown },
];

// Subset for bottom-tab on mobile (5 most-used destinations)
const BOTTOM_TABS = [
  { path: '/dashboard', label: 'Home', icon: House },
  { path: '/workouts', label: 'Workouts', icon: Barbell },
  { path: '/ai-plan', label: 'AI Plan', icon: Brain },
  { path: '/stress-reset', label: 'Reset', icon: Wind },
  { path: '/profile', label: 'Profile', icon: User },
];

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' && !navigator.onLine);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Hide bottom tabs during an active workout session (full-screen experience)
  const hideBottomNav = location.pathname.startsWith('/workouts/player');

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="app-layout">
      {/* Offline banner */}
      {isOffline && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-[#FF9500] text-black text-xs font-bold uppercase tracking-wider py-2 px-4 flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiSlash weight="bold" className="w-4 h-4" /> Offline — Cached workouts still available
        </div>
      )}
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden cursor-pointer"
          onClick={(e) => { e.stopPropagation(); setSidebarOpen(false); }}
          role="button"
          aria-label="Close menu"
          tabIndex={-1}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] border-r border-white/10 transform transition-transform duration-200 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Lightning weight="fill" className="text-[#FF3B30] w-6 h-6" />
              <span className="font-['Barlow_Condensed'] text-lg font-black uppercase tracking-tight">GRIT.FIT</span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-[#71717A] hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 ${active ? 'bg-[#FF3B30]/10 text-[#FF3B30] border-l-2 border-[#FF3B30]' : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'}`}
                >
                  <item.icon weight={active ? 'fill' : 'regular'} className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 mt-4 pt-4 border-t border-white/5 ${location.pathname === '/admin' ? 'bg-[#FF3B30]/10 text-[#FF3B30] border-l-2 border-[#FF3B30]' : 'text-[#FF9500] hover:text-white hover:bg-white/5'}`}
              >
                <Crown weight={location.pathname === '/admin' ? 'fill' : 'regular'} className="w-5 h-5" />
                Admin
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-white/10">
            <Link
              to="/profile"
              data-testid="nav-profile"
              className="flex items-center gap-3 px-3 py-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
            >
              <div className="w-8 h-8 bg-[#FF3B30]/20 flex items-center justify-center">
                <User weight="bold" className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs text-[#71717A] truncate">{user?.email}</div>
              </div>
            </Link>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full mt-2 text-left px-3 py-2 text-sm text-[#71717A] hover:text-[#FF3B30] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-3 flex items-center justify-between lg:justify-end">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white min-h-[44px] min-w-[44px] flex items-center justify-center -ml-2" data-testid="mobile-menu-toggle" aria-label="Open menu">
            <List className="w-6 h-6" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2 lg:hidden">
            <Lightning weight="fill" className="text-[#FF3B30] w-5 h-5" />
            <span className="font-['Barlow_Condensed'] text-base font-black uppercase tracking-tight">GRIT.FIT</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="label-tactical text-[#A1A1AA] hidden sm:inline">{user?.fitness_level}</span>
          </div>
        </header>
        <div className={`p-4 sm:p-6 lg:p-8 ${hideBottomNav ? '' : 'pb-24 lg:pb-8'}`}>
          {children}
        </div>
      </main>

      {/* Mobile Bottom Tab Navigation */}
      {!hideBottomNav && (
        <nav
          className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="bottom-nav"
          aria-label="Primary"
        >
          <div className="grid grid-cols-5">
            {BOTTOM_TABS.map(tab => {
              const active = location.pathname === tab.path;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
                  className={`flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] transition-colors ${active ? 'text-[#FF3B30]' : 'text-[#71717A] hover:text-white'}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <tab.icon weight={active ? 'fill' : 'regular'} className="w-5 h-5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">{tab.label}</span>
                  {active && <div className="absolute top-0 h-0.5 w-8 bg-[#FF3B30]" />}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
